// File: src/views/AdvicePage.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchSymptomsByDisease } from '../api/cropService';
import html2canvas from 'html2canvas';
import { getDiseaseFullDetailsById } from '../utils/diseaseUtils';

const AdvicePage = () => {
  const { t } = useTranslation();
  const { cid } = useParams();
  const cropId = parseInt(cid, 10);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [advice, setAdvice] = useState(null);
  const [diseaseDetails, setDiseaseDetails] = useState(null);
  const [cropName, setCropName] = useState("Cotton");
  
  // Function to parse URL parameters
  const getUrlParams = () => {
    const pathSegments = window.location.pathname.split('/');
    const cropId = pathSegments[pathSegments.indexOf('crops') + 1];
    const diseaseId = pathSegments[pathSegments.indexOf('disease') + 1];
    return { cropId, diseaseId };
  };
  
  useEffect(() => {
    const fetchAdviceData = async () => {
      try {
        setLoading(true);
        const { cropId, diseaseId } = getUrlParams();

        // Fetch all necessary data
        const symptomsData = await fetchSymptomsByDisease(diseaseId);
        console.log("Fetched symptoms data:", symptomsData);

        const details = await getDiseaseFullDetailsById(diseaseId, cropName);
        console.log("Disease details:", details);
        
        const savedDiagnosis = sessionStorage.getItem('diagnosisData');
        let diagnosisData = null;
        
        if (savedDiagnosis) {
          diagnosisData = JSON.parse(savedDiagnosis);
          console.log("Diagnosis Data:", diagnosisData);

          // Combine all image sources
          const images = [
            ...(details.diseaseDetails?.images || [])
          ];

          setDiseaseDetails(details.diseaseDetails);
          setAdvice({
            ...diagnosisData,
            imageUrl: images,
            chemical_name: diagnosisData.scientificName || diagnosisData.chemicalName || diagnosisData.chemical_name,
            otherSymptoms: symptomsData || []
          });

          if (diagnosisData.cropName) {
            setCropName(diagnosisData.cropName);
          }
          
        } else {
          console.log("No diagnosis data found in sessionStorage");
        }
      } catch (err) {
        console.error("Error in fetching advice data:", err);
        setError(t('advice.noAdviceData'));
        
        // Set basic fallback data for all errors
        setAdvice({
          chemical_name: 'Unknown Disease',
          chemicalAdvice: 'Please consult with an agricultural expert.',
          "Other symptoms": "<No symptoms data available>",
          "Symptoms confirmed by you": "<No confirmed symptoms data available>",
          imageUrl: []
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchAdviceData();
  }, [t]);

  // Back handler that preserves the symptom flow state
  const handleBack = () => {
    // Get the saved diagnosis data which contains the symptom history
    const savedDiagnosis = sessionStorage.getItem('diagnosisData');
    if (savedDiagnosis) {
      try {
        const diagnosisData = JSON.parse(savedDiagnosis);
        const confirmedSymptoms = diagnosisData.confirmedSymptoms || [];
        
        console.log("Confirmed symptoms:", confirmedSymptoms);
        
        if (confirmedSymptoms.length > 0) {
          // Remove the last selected symptom to go back one step
          const previousHistory = confirmedSymptoms.slice(0, -1);
          
          // Calculate the current level based on remaining symptoms
          const currentLevel = Math.max(1, previousHistory.length + 1);
          
          console.log("Previous history:", previousHistory);
          console.log("Current level:", currentLevel);

          // Save the symptom flow state to sessionStorage so SymptomsPage can restore it
          const symptomFlowState = {
            history: previousHistory, // All symptoms except the last one
            currentLevel: currentLevel,
            returnFromAdvice: true,
            allSelectedSymptoms: confirmedSymptoms, // All symptoms that were selected (for highlighting)
            lastSelectedSymptom: confirmedSymptoms[confirmedSymptoms.length - 1] // The symptom that was just removed
          };
          
          sessionStorage.setItem('symptomFlowState', JSON.stringify(symptomFlowState));
          console.log("Saved symptom flow state:", symptomFlowState);
        } else {
          // No symptoms to go back to, just set basic state
          const symptomFlowState = {
            history: [],
            currentLevel: 1,
            returnFromAdvice: true,
            allSelectedSymptoms: [],
            lastSelectedSymptom: null
          };
          
          sessionStorage.setItem('symptomFlowState', JSON.stringify(symptomFlowState));
        }
      } catch (err) {
        console.error('Error parsing diagnosis data:', err);
        // Fallback state
        const symptomFlowState = {
          history: [],
          currentLevel: 1,
          returnFromAdvice: true,
          allSelectedSymptoms: [],
          lastSelectedSymptom: null
        };
        
        sessionStorage.setItem('symptomFlowState', JSON.stringify(symptomFlowState));
      }
    } else {
      // No diagnosis data, set basic state
      const symptomFlowState = {
        history: [],
        currentLevel: 1,
        returnFromAdvice: true,
        allSelectedSymptoms: [],
        lastSelectedSymptom: null
      };
      
      sessionStorage.setItem('symptomFlowState', JSON.stringify(symptomFlowState));
    }
    
    // Navigate back to symptoms page with the cropId
    navigate(`/crops/${cropId}/symptoms`);
  };
  
  const handleStartFresh = () => {
    window.location.href = `/`;
  };
  
  // Helper function to parse symptom strings
  const parseSymptoms = (symptomString) => {
    if (!symptomString) return [];
    return symptomString
      .split('><')
      .map(s => s.replace(/[<>]/g, ''))
      .filter(s => s.trim().length > 0);
  };

  // Helper function to get confirmed symptoms from the data structure
  const getConfirmedSymptoms = () => {
    if (!advice) return [];
    
    // Check if confirmedSymptoms array exists
    if (advice.confirmedSymptoms && Array.isArray(advice.confirmedSymptoms)) {
      return advice.confirmedSymptoms.map(symptom => symptom.name || symptom);
    }
    
    // Fallback to the old format
    if (advice["Symptoms confirmed by you"]) {
      return parseSymptoms(advice["Symptoms confirmed by you"]);
    }
    
    return [];
  };

  // Helper function to get other symptoms
  const getOtherSymptoms = () => {
    if (!advice) return [];
    
    // Check if otherSymptoms array exists
    if (advice.otherSymptoms && Array.isArray(advice.otherSymptoms)) {
      return advice.otherSymptoms;
    }
    
    // Fallback to the old format
    if (advice["Other symptoms"]) {
      return parseSymptoms(advice["Other symptoms"]);
    }
    
    return [];
  };

  // Function to handle image error
  const handleImageError = (e) => {
    console.log('Image failed to load:', e.target.src);
    e.target.src = '/api/placeholder/100/100';
    e.target.alt = t('advice.imageNotAvailable');
  };
  
  // Render images in a grid layout
  const renderImages = () => {
    const images = Array.isArray(advice?.imageUrl) ? advice.imageUrl : [];
    if (images.length === 0) {
      return (
        <p className="text-center text-gray-500">{t('advice.noImagesAvailable')}</p>
      );
    }

    return (
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap justify-center gap-4">
          {images.map((url, i) => (
            <img
              key={i}
              src={url}
              alt={`Sample ${i + 1}`}
              className="w-24 h-24 md:w-32 md:h-32 object-cover rounded-lg shadow-sm"
              onError={handleImageError}
            />
          ))}
        </div>
      </div>
    );
  };
  const handleSaveAsImage = async () => {
    const adviceElement = document.getElementById('advice-content');
    if (!adviceElement) return;
    console.log("Save as Image");
    
    try {
      const canvas = await html2canvas(adviceElement, { scale: 2 });
      const image = canvas.toDataURL('image/png');
  
      const link = document.createElement('a');
      link.href = image;
      link.download = `crop-advice-${cropName.toLowerCase()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to save as image:', err);
    }
  };
  
  const handleSaveAsPDF = async () => {
    window.print();
  };

  const renderChemicalAdvice = () => {
    let text = advice?.chemicalAdvice;
    if (!text) return null;

    // Remove bullet prefix if exists
    text = text.replace(/^•\s*/, '').trim();

    // Split using "Note:" (case insensitive, ignoring spaces around it)
    const parts = text.split(/note\s*:\s*/i);
    const mainText = parts[0].trim();
    const noteText = parts[1] ? parts[1].trim() : null;

    // Extract numbered pattern like (1). ... (2). ...
    const itemMatches = mainText.match(/\(\d+\)\.[^()]+/g);

    // If no numbered items found, show as paragraph
    if (!itemMatches) {
      return (
        <div className="ml-6">
          <p className="text-lg">• {mainText}</p>
          {noteText && (
            <p className="mt-4 text-base text-gray-700">
              <span className="font-semibold">Note:</span> {noteText}
            </p>
          )}
        </div>
      );
    }

    // Clean numbered items
    const items = itemMatches.map(item =>
      item.replace(/\(\d+\)\.\s*/, '').trim()
    );

    return (
      <div className="ml-6">
        <ol className="list-decimal ml-4 text-lg space-y-2">
          {items.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ol>

        {noteText && (
          <p className="mt-4 text-base text-gray-700">
            <span className="font-semibold">Note:</span> {noteText}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="w-full min-h-screen px-6 py-4">
      <div className="flex justify-between mb-6">
        <div className="flex items-center">
          <button onClick={handleBack} className="text-blue-600 hover:underline text-lg font-medium">
            {t('common.back')}
          </button>
        </div>
        <button onClick={handleStartFresh} className="text-blue-600 hover:underline text-lg font-medium">
          {t('common.startFresh')}
        </button>
      </div>
      <div className="w-full px-4 py-8">

      <h1 id="pageTitle" className="text-2xl font-bold text-center text-gray-800 mb-4">
        {t('advice.problemFound', { cropName })}
      </h1>
      
      {loading ? (
        <div className="text-center p-8">
          <p className="text-xl">{t('advice.loadingAdvice')}</p>
        </div>
      ) : error ? (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-6 mb-6">
          <p className="text-lg">{error}</p>
          {/* Show advice data even if there's an error */}
          {advice && (
            <div id="advice-content" className="space-y-8 mt-6">
              {/* Disease Name */}
              <div className="text-center bg-gray-100 rounded-lg p-4 max-w-4xl mx-auto mb-8">
                  <span className="font-medium">{t('advice.nameOfProblem')}: </span>
                  <span className="text-red-600 font-medium">{advice.chemical_name}</span>
              </div>
              
              {/* Sample Images */}
              <div className="border-b pb-6">
                <h3 className="text-center font-medium mb-6 text-xl">{t('advice.sampleImages')}</h3>
                {renderImages()}
              </div>
              
              {/* Chemical Advice */}
              <div className="border-b pb-6">
                <h3 className="text-center font-medium mb-6 text-xl">{t('advice.chemicalAdvice')}</h3>
                {/* <p className="ml-6 text-lg">{advice.chemicalAdvice}</p> */}
                {renderChemicalAdvice()}
              </div>
              
              {/* Confirmed Symptoms */}
              <div className="border-b pb-6">
                <h3 className="text-center font-medium mb-6 text-xl">{t('advice.confirmedSymptoms')}</h3>
                <ul className="list-disc ml-12 text-lg space-y-2">
                  {getConfirmedSymptoms().map((symptom, idx) => (
                    <li key={idx}>{symptom}</li>
                  ))}
                </ul>
                {getConfirmedSymptoms().length === 0 && (
                  <p className="ml-6 text-lg text-gray-500">{t('advice.noConfirmedSymptoms')}</p>
                )}
              </div>
              
              {/* Other Symptoms */}
              <div className="border-b pb-6">
                <h3 className="text-center font-medium mb-6 text-xl">{t('advice.otherSymptoms')}</h3>
                <ul className="list-disc ml-12 text-lg space-y-2">
                  {getOtherSymptoms()
                    .filter(symptom => !getConfirmedSymptoms().includes(symptom.name || symptom.sid))
                    .map((symptom, idx) => (
                      <li key={symptom.sid || idx}>{symptom.name || symptom}</li>
                    ))
                  }
                </ul>
                {getOtherSymptoms().length === 0 && (
                  <p className="ml-6 text-lg text-gray-500">{t('advice.noOtherSymptoms')}</p>
                )}
              </div>
              
              {/* Important Note */}
              <div className="mb-8">
                <details>
                  <summary className="cursor-pointer font-medium text-lg">{t('advice.importantNote')}</summary>
                  <div className="p-4 text-base text-gray-700">
                    <ol className="list-decimal ml-6 space-y-2">
                      <li>{t('advice.notes.note1')}</li>
                      <li>{t('advice.notes.note2')}</li>
                      <li>{t('advice.notes.note3')}</li>
                      <li>{t('advice.notes.note4')}</li>
                      <li>{t('advice.notes.note5')}</li>
                    </ol>
                  </div>
                </details>
              </div>
              
              {/* Export Buttons */}
              <div className="flex justify-center gap-6 pt-4">
                <button 
                  onClick={handleSaveAsImage}
                  className="bg-green-500 hover:bg-green-600 text-white px-10 py-3 rounded text-lg font-medium"
                >
                  {t('advice.saveAsImage')}
                </button>
                <button 
                  onClick={handleSaveAsPDF}
                  className="bg-green-500 hover:bg-green-600 text-white px-10 py-3 rounded text-lg font-medium"
                >
                  {t('advice.saveAsPdf')}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : advice ? (
        <div id="advice-content" className="max-w-6xl mx-auto bg-white rounded-lg p-6 shadow-sm">
          {/* Disease Name */}
          <div className="border-t border-b py-6 bg-gray-100 rounded-lg">
            <h2 className="text-center text-xl">
              <span className="font-medium">{t('advice.nameOfProblem')} </span>
              <span className="text-red-600 font-medium">{advice.chemical_name}</span>
            </h2>
          </div>
          
          {/* Sample Images */}
          <div className="border-b pb-6">
            <h3 className="text-center font-medium mb-6 text-xl">{t('advice.sampleImages')}</h3>
            {renderImages()}
          </div>
          
          {/* Chemical Advice */}
          <div className="border-b pb-6">
            <h3 className="text-center font-medium mb-6 text-xl">{t('advice.chemicalAdvice')}</h3>
            {/* <p className="ml-6 text-lg">{advice.chemicalAdvice}</p> */}
            {renderChemicalAdvice()}
          </div>
          
          {/* Confirmed Symptoms */}
          <div className="border-b pb-6">
            <h3 className="text-center font-medium mb-6 text-xl">{t('advice.confirmedSymptoms')}</h3>
            <ul className="list-disc ml-12 text-lg space-y-2">
              {getConfirmedSymptoms().map((symptom, idx) => (
                <li key={idx}>{symptom}</li>
              ))}
            </ul>
            {getConfirmedSymptoms().length === 0 && (
              <p className="ml-6 text-lg text-gray-500">{t('advice.noConfirmedSymptoms')}</p>
            )}
          </div>
          
          {/* Other Symptoms */}
          <div className="border-b pb-6">
            <h3 className="text-center font-medium mb-6 text-xl">{t('advice.otherSymptoms')}</h3>
            <ul className="list-disc ml-12 text-lg space-y-2">
              {getOtherSymptoms()
                .filter(symptom => !getConfirmedSymptoms().includes(symptom.name || symptom.sid))
                .map((symptom, idx) => (
                  <li key={symptom.sid || idx}>{symptom.name || symptom}</li>
                ))
              }
            </ul>
            {getOtherSymptoms().length === 0 && (
              <p className="ml-6 text-lg text-gray-500">{t('advice.noOtherSymptoms')}</p>
            )}
          </div>
          
          {/* Important Note */}
          <div className="mb-8">
            <details>
              <summary className="cursor-pointer font-medium text-lg">{t('advice.importantNote')}</summary>
              <div className="p-4 text-base text-gray-700">
                <ol className="list-decimal ml-6 space-y-2">
                  <li>{t('advice.notes.note1')}</li>
                  <li>{t('advice.notes.note2')}</li>
                  <li>{t('advice.notes.note3')}</li>
                  <li>{t('advice.notes.note4')}</li>
                  <li>{t('advice.notes.note5')}</li>
                </ol>
              </div>
            </details>
          </div>
          
          {/* Export Buttons */}
          <div className="flex justify-center gap-6 pt-4">
            <button 
              onClick={handleSaveAsImage}
              className="bg-green-500 hover:bg-green-600 text-white px-10 py-3 rounded text-lg font-medium"
            >
              {t('advice.saveAsImage')}
            </button>
            <button 
              onClick={handleSaveAsPDF}
              className="bg-green-500 hover:bg-green-600 text-white px-10 py-3 rounded text-lg font-medium"
            >
              {t('advice.saveAsPdf')}
            </button>
          </div>
        </div>
      ) : (
        <div className="text-center p-8">
          <p className="text-xl">{t('advice.noAdviceData')}</p>
        </div>
      )}
    </div>
    </div>
  );
};

export default AdvicePage;